/**
 * Vesper Explore Service (Shared)
 *
 * Backend calls for Ark Explore mode — discovery of events and live
 * moments near the user.
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import type {
  ExploreResponse,
  ExploreInitialResponse,
  SavedEventSummary,
} from '../types/ark.types';

/** Phase B2 — segmented control filter passed to /explore. */
export interface ExploreClientFilters {
  remoteMode?: 'local' | 'mixed' | 'remote';
}

export async function sendExploreMessage(
  message: string,
  userLocation?: { lat: number; lng: number } | null,
  filters?: ExploreClientFilters,
): Promise<ExploreResponse> {
  const res = await apiClient.post<{ data: ExploreResponse }>(API.ARK_EXPLORE, {
    message,
    location: userLocation ?? undefined,
    ...(filters ? { filters } : {}),
  });
  return res.data.data;
}

/**
 * Phase B1 — Dynamic Explore-mode pills derived from nearby founder events.
 * Cached server-side per (user, geo-rounded) for 1h. Falls back to an empty
 * array on fetch failure — clients should display their static chip set
 * when this returns empty.
 */
export async function getSuggestedPills(
  userLocation: { lat: number; lng: number },
): Promise<string[]> {
  const params = new URLSearchParams({
    lat: String(userLocation.lat),
    lng: String(userLocation.lng),
  });
  try {
    const res = await apiClient.get<{ data: { pills: string[] } }>(
      `/cleo/explore/suggested-pills?${params.toString()}`,
    );
    return res.data.data.pills ?? [];
  } catch {
    return [];
  }
}

export async function getExploreInitial(
  userLocation?: { lat: number; lng: number } | null,
): Promise<ExploreInitialResponse> {
  const params = new URLSearchParams();
  if (userLocation) {
    params.set('lat', String(userLocation.lat));
    params.set('lng', String(userLocation.lng));
  }
  const qs = params.toString();
  const url = qs ? `${API.ARK_EXPLORE_INITIAL}?${qs}` : API.ARK_EXPLORE_INITIAL;
  const res = await apiClient.get<{ data: ExploreInitialResponse }>(url);
  return res.data.data;
}

export async function saveEvent(eventId: string): Promise<void> {
  await apiClient.post(API.PUBLIC_EVENT_SAVE(eventId));
}

export async function unsaveEvent(eventId: string): Promise<void> {
  await apiClient.delete(API.PUBLIC_EVENT_SAVE(eventId));
}

export async function getSavedEvents(): Promise<SavedEventSummary[]> {
  const res = await apiClient.get<{ data: SavedEventSummary[] }>(API.PUBLIC_EVENTS_SAVED);
  return res.data.data;
}
